import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MonetisationApi } from 'src/api';
import { useActivityCheck } from 'src/hooks/useActivityCheck';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import {
  getPresentationPronunciationAttemptsRemaining,
  PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL,
  PRESENTATION_SCREEN_PRONUNCIATION_TOKEN,
  recordPresentationPronunciationConsumption,
} from './presentationPronunciationAttempts';

export const usePresentationPronunciationAttempts = () => {
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const queryClient = useQueryClient();
  const [, setVersion] = useState(0);

  const { isLoading, isError, isLocked, isRationed } = useActivityCheck({
    tokenType: PRESENTATION_SCREEN_PRONUNCIATION_TOKEN,
  });

  const remaining = !isRationed
    ? PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL
    : isLocked
      ? 0
      : getPresentationPronunciationAttemptsRemaining(user);

  /** Returns the attempts left after this one, or `null` when it wasn't counted. */
  const consumeAttempt = useCallback((): number | null => {
    if (!isRationed || isLocked || !languagePairId) {
      return null;
    }

    const remainingAfter = recordPresentationPronunciationConsumption(user);
    setVersion(current => current + 1);
    void MonetisationApi.consumeFeatureToken(
      languagePairId,
      PRESENTATION_SCREEN_PRONUNCIATION_TOKEN,
    ).finally(() => {
      void queryClient.invalidateQueries({
        queryKey: ['getFeaturesTokensAvailability', languagePairId],
      });
    });

    return remainingAfter;
  }, [isLocked, isRationed, languagePairId, queryClient, user]);

  return {
    remaining,
    isLoading,
    isError,
    isLocked: isRationed && (isLocked || remaining === 0),
    isRationed,
    consumeAttempt,
  };
};
